import { InitMarginAddresses } from '@1delta/calldata-sdk'
import { parseRawAmount } from '../../../utils/parsing'
import { isFailedCall } from '../../../utils/multicall'
import { createMultiAccountTypeUserState } from '../utils/createMultiAccountStyleUserState'
import { InitGetUserPositionDataIndexes } from '../../../types/lender/init-types'
import { LenderCrossPoolMeta, UserData } from '../utils/types'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMarketUid } from '../../../utils/marketUid'

/**
 * Parse a single getUserPositionData result for one broker.
 * Returns { result, modes, histData } that can be merged across brokers.
 */
function parseSingleBrokerData(
  data: any,
  validOwner: string,
  brokerAddress: string,
  lender: string,
  chainId: string,
  metaMap?: LenderCrossPoolMeta,
) {
  // A failed read carries no positions — return an empty slice so the other
  // broker's (successful) positions still surface.
  if (isFailedCall(data)) {
    return { result: {}, modes: {}, histData: {} }
  }

  const indexes = data[InitGetUserPositionDataIndexes.posMeta]
    .map((e: any, i: number) => ({ e, i }))
    .filter((a: any) => a.e.owner.toLowerCase() === validOwner)
    .map((a: any) => a.i)

  const histData: any = {}
  const modes: { [posId: string]: any } = {}

  let allPools: { [s: string]: string } = {}

  const result: any = Object.assign(
    {},
    ...indexes.map((index: any) => {
      const entry = data[InitGetUserPositionDataIndexes.posMeta][index]
      const mode = entry.mode
      const id = entry.posId.toString()
      modes[id] = mode
      // this is keyed by pool
      const collatData = Object.assign(
        {},
        ...data[InitGetUserPositionDataIndexes.collaterals][index].map(
          (c: any) => {
            const pool = c.pool.toLowerCase()
            allPools[pool] = c.underlying.toLowerCase()
            const key = createMarketUid(chainId, lender, pool)
            const metaEntity = metaMap?.[key]!
            const decimals = metaEntity.asset?.decimals ?? 18
            const depositsRaw = c.amount.toString()
            return {
              [pool]: {
                deposits: parseRawAmount(depositsRaw, decimals),
              },
            }
          },
        ),
      )
      // this is keyed by pool
      const debtData = Object.assign(
        {},
        ...data[InitGetUserPositionDataIndexes.debts][index].map((c: any) => {
          const pool = c.pool.toLowerCase()
          allPools[pool] = c.underlying.toLowerCase()
          const key = createMarketUid(chainId, lender, pool)
          const metaEntity = metaMap?.[key]!
          const decimals = metaEntity.asset?.decimals ?? 18
          return {
            [pool]: {
              debt: parseRawAmount(c.amount.toString(), decimals),
            },
          }
        }),
      )
      // sub account to asset data (mode stored separately in modes map)
      return {
        [id]: Object.assign(
          {},
          ...Object.entries(allPools).map(
            ([pool, underlying]: [string, string]) => {
              const key = createMarketUid(chainId, lender, pool)
              const metaEntity = metaMap?.[key]!
              // prices
              const price = getDisplayPrice(metaEntity)
              const oPrice = getOraclePrice(metaEntity)
              const priceHist = metaEntity?.price?.priceUsd24h ?? price
              if (!histData[id])
                histData[id] = {
                  totalDebt24h: 0,
                  totalDeposits24h: 0,
                }
              const debt = debtData[pool]?.debt ?? '0'
              const deposits = collatData[pool]?.deposits ?? '0'

              histData[id].totalDebt24h += Number(debt) * priceHist
              histData[id].totalDeposits24h += Number(deposits) * priceHist

              // asset to data
              return {
                [key]: {
                  marketUid: key,
                  underlying,
                  deposits: deposits,
                  debt: debt,
                  debtStable: '0',
                  debtStableUSD: 0,
                  depositsUSD: price * Number(deposits),
                  debtUSD: price * Number(debt),
                  depositsUSDOracle: oPrice * Number(deposits),
                  debtStableUSDOracle: 0,
                  debtUSDOracle: oPrice * Number(debt),
                  collateralEnabled: true,
                  isAllowed:
                    entry.approved.toLowerCase() ===
                    brokerAddress.toLowerCase(),
                },
              }
            },
          ),
        ),
      }
    }),
  )

  return { result, modes, histData }
}

export const getInitUserDataConverter = (
  lender: string,
  chainId: string,
  account: string,
  metaMap?: LenderCrossPoolMeta,
): [(data: any[]) => UserData | undefined, number] => {
  const validOwner = account.toLowerCase()
  const legacyBroker =
    InitMarginAddresses.getLegacyFlashAggregator(chainId) ?? ''
  const currentBroker = InitMarginAddresses.getFlashAggregator(chainId) ?? ''
  const expectedNumberOfCalls = 2
  return [
    (_data: any[]) => {
      if (_data.length !== expectedNumberOfCalls) {
        return undefined
      }

      // Parse positions from legacy and current broker calls
      const legacy = parseSingleBrokerData(
        _data[0],
        validOwner,
        legacyBroker,
        lender,
        chainId,
        metaMap,
      )
      const current = parseSingleBrokerData(
        _data[1],
        validOwner,
        currentBroker,
        lender,
        chainId,
        metaMap,
      )

      // Merge — posIds are unique across brokers
      const result = { ...legacy.result, ...current.result }
      const modes = { ...legacy.modes, ...current.modes }
      const histData = { ...legacy.histData, ...current.histData }

      const payload = {
        chainId,
        lendingPositions: result,
        modes,
        account,
      }

      const userState = createMultiAccountTypeUserState(
        payload,
        metaMap!,
        histData,
      )
      return userState
    },
    expectedNumberOfCalls,
  ]
}
